import * as React from "react";

import { useNavigate } from "react-router-dom";

import { Button } from "ui-components";
import logo from "../../images/logo-black.svg";
import appsIcon from "../../images/ico-apps.svg";
import appsGreenIcon from "../../images/ico-apps-green.svg";
import logoPulseway from "../../images/logo-pulseway.png";
import logoStart from "../../images/logo-vega-apps.png";
import logoGitlab from "../../images/logo-gitlab.png";
import logoPassbolt from "../../images/logo-passbolt.png";
import logoGmail from "../../images/logo-gmail.png";
import logoThrivea from "../../images/logo-thrivea.png";
import logoDobrok from "../../images/logo-dobrok.png";
import { AppRoutes } from "../../config";

import { TokenInfoResponse } from "api/generated";
import { useEffect, useState } from "react";
import { getTokenInfo } from "redux/actions/accessControlActions";
import { useAppDispatch } from "redux/hooks";
import { handleErrors } from "utils/helpers";

export const MainNav: React.FC = () => {
  const dispatch = useAppDispatch();
  const [tokenInfo, setTokenInfo] = useState<TokenInfoResponse | undefined>();

  useEffect(() => {
    dispatch(getTokenInfo()).unwrap().then(setTokenInfo).catch(handleErrors);
  }, [dispatch]);

  const navigate = useNavigate();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const handleHome = () => {
    navigate(AppRoutes.createRouteForEmployees("home"));
    setIsOpen(!isOpen);
  };
  const handleInactive = () => {
    navigate(AppRoutes.createRouteForEmployees("inactive"));
    setIsOpen(!isOpen);
  };

  const handleStillInside = () => {
    navigate(AppRoutes.createRouteForEmployees("inside"));
    setIsOpen(!isOpen);
  };

  const hamburgerClass = isOpen
    ? "header__hamburger js-header-hamburger header__hamburger--open"
    : "header__hamburger js-header-hamburger";

  const contentClass = isOpen
    ? "header__actions js-header-actions header__actions--open"
    : "header__actions js-header-actions";

  const handleToogle = () => {
    setIsOpen(!isOpen);
  };

  const handleNavigate = () => {
    navigate("/");
  };

  const onLogout = () => {
    localStorage.removeItem("persist:root");
    navigate(AppRoutes.createRouteForLogin());
  };

  const INTERNAL_APPS = [
    {
      url: "https://timesheet.vegait.rs",
      image: <><span className="initials">T</span><span>Timesheet</span></>
    },
    {
      url: "https://people.vegait.rs/",
      image: <><span className="initials">P</span><span>People</span></>
    },
    {
      url: "https://workspace.vegait.rs",
      image: <><span className="initials">W</span><span>Workspace</span></>
    },
    {
      url: "https://boards.vegait.rs",
      image: <><span className="initials">B</span><span>Boards</span></>
    },
    {
      url: "https://docs.vegait.rs",
      image: <><span className="initials">D</span><span>Docs</span></>
    },
    {
      url: "https://roletne.vegait.rs/",
      image: <><span className="initials">R</span><span>Blinds</span></>
    },
    {
      url: "https://library.vegait.rs/",
      image: <><span className="initials">L</span><span>Library</span></>
    }
  ]

  const INTERNAL_SERVICES = [
    {
      url: "https://start.vegait.rs/",
      image: <><img src={logoStart} alt="" role="presentation"/><span>Start</span></>
    },
    {
      url: "https://git.vegaitsourcing.rs/",
      image: <><img src={logoGitlab} alt="" role="presentation"/><span>Gitlab</span></>
    },
    {
      url: "https://passwords.vegait.rs/",
      image: <><img src={logoPassbolt} alt="" role="presentation"/><span>Passbolt</span></>
    }
  ]

  const EXTERNAL_APPS = [
    {
      url: "https://mail.google.com",
      image: <><img src={logoGmail} alt="" role="presentation"/><span>Google Workspace</span></>
    },
    {
      url: "https://app.thrivea.com/",
      image: <><img src={logoThrivea} alt="" role="presentation"/><span>Thrivea</span></>
    },
    {
      url: "https://portal.dobrok.rs/",
      image: <><img src={logoDobrok} alt="" role="presentation"/><span>Dobrok</span></>
    },
    {
      url: "https://psa.pulseway.com/Gateway.aspx?enc=cFNzluKg%2fG1NFRXcMvpeVGojaaXVfjRkmuUr2ohhlrc%3d",
      image: <><img src={logoPulseway} alt="" role="presentation"/><span>Pulseway</span></>
    }
  ]

  function getInitials() {
    if (!tokenInfo?.name) return "";
    return tokenInfo?.name
      .trim()
      .split(/\s+/)          // split on any whitespace
      .map(word => word[0])  // take the first letter of each word
      .join("")
      .toUpperCase();
  }

  return (
    <header className="header">
      <button onClick={handleNavigate} className="header__logo">
        <img src={logo} alt="" className="header__logo-img" />
      </button>
      <div className={contentClass}>
        <div className="header__buttons">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="header__nav-link"
          >
            Workspace
          </button>
          <button
            type="button"
            onClick={handleStillInside}
            className="header__nav-link"
          >
            Still inside
          </button>
          <button
            type="button"
            onClick={handleHome}
            className="header__nav-link"
          >
            Working from home
          </button>
          <button
            type="button"
            onClick={handleInactive}
            className="header__nav-link"
          >
            Inactive colleagues
          </button>
        </div>
      </div>
      <div className="header__tools">
        <button onClick={handleToogle} className={hamburgerClass}>
          <span className="header__hamburger__bar"></span>
          <span className="sr-only">Menu</span>
        </button>
        <div className="header__apps">
          <button type="button" className="header__apps-btn">
            <img src={appsIcon} alt="" className="header__apps-btn-icon" />
            <img src={appsGreenIcon} alt="" className="header__apps-btn-icon header__apps-btn-icon--green"/>
            <span className="sr-only">Apps</span>
          </button>
          <ul className="header__apps-list">
            <li className="header__apps-divider"><span>Internal apps</span></li>
            {INTERNAL_APPS.map((app, key) => <li key={key} className="header__apps-item">
                <a href={app.url} target="_blank" className="header__apps-link">{app.image}</a>
            </li>)}

            <li className="header__apps-divider"><span>Internal services</span></li>
            {INTERNAL_SERVICES.map((app, key) => <li key={key} className="header__apps-item">
                <a href={app.url} target="_blank" className="header__apps-link">{app.image}</a>
            </li>)}

            <li className="header__apps-divider"><span>External services</span></li>
            {EXTERNAL_APPS.map((app, key) => <li key={key} className="header__apps-item">
                <a href={app.url} target="_blank" className="header__apps-link">{app.image}</a>
            </li>)}
          </ul>
        </div>
        <div className="header__user">
          <button type="button" className="header__user-btn">{getInitials()}</button>
          <ul className="header__user-list">
            <li className="header__user-item header__user-item--profile">
              <span className="header__user-avatar">{getInitials()}</span>
              <span className="header__user-name">{tokenInfo?.name}</span>
            </li>
            <li className="header__user-item">
              <Button onClick={onLogout} text={"Log out"} />
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
};
